import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  BadgeOutlined,
  ExpandMore,
  GitHub,
  SearchOutlined,
  StorageOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  TextField,
} from "@mui/material";

import { useGetActiveOrganizationsQuery } from "state/rtkQuery/org/orgQueries";
import { useGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import { useLazyGetAdCheckEmailQuery, useLazyGetUserQuery, useLazyGetUserGitHubMembershipQuery } from "state/rtkQuery/user/userQueries";

import UserOrgSearch from "components/UserOrgSearch/UserOrgSearch";
import ADSection from "./ADSection";
import GitHubSection from "./GitHubSection";

import "../EcrOpsLayout.css";
import "./EcrOpsUnifiedSearch.css";

// ── Section header status chips ───────────────────────────────────────────────

const SectionStatus = ({ loading, found, notFound, error, issueCount = 0 }) => {
  if (loading) return <CircularProgress size={14} sx={{ color: "#86BC25", ml: 1.5 }} />;
  if (error) return <Chip label="Error" size="small" className="ecr-us__chip ecr-us__chip--error" />;
  if (notFound) return <Chip label="Not Found" size="small" className="ecr-us__chip ecr-us__chip--neutral" />;
  if (found) return (
    <>
      <Chip label="Found" size="small" className="ecr-us__chip ecr-us__chip--ok" />
      {issueCount > 0 && (
        <Chip
          label={`${issueCount} org ${issueCount === 1 ? "issue" : "issues"}`}
          size="small"
          className="ecr-us__chip ecr-us__chip--warn"
        />
      )}
    </>
  );
  return null;
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function caseMatch(a, b) {
  return a?.toLowerCase() === b?.toLowerCase();
}

// ── Main component ────────────────────────────────────────────────────────────

const EcrOpsUnifiedSearch = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchEmail = searchParams.get("email") || "";
  const [inputEmail, setInputEmail] = useState(searchEmail);

  // ── Arbiter ──
  // Falls back to the default (first) platform until the ECR Ops platform
  // selector (EcrOpsLayout) has resolved a selection.
  const { data: platformData } = useGetPlatformsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const selectedPlatformId = useSelector((state) => state.ecrOpsPlatform?.selectedPlatformId);
  const activePlatformId = selectedPlatformId || platformData?._id;
  const { data: allOrgs } = useGetActiveOrganizationsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [triggerArbiter, { data: arbiterUsers, isFetching: arbiterLoading, isUninitialized: arbiterUninitialized }] =
    useLazyGetUserQuery();
  const [arbiterUser, setArbiterUser] = useState(null);
  const [arbiterOrgs, setArbiterOrgs] = useState([]);
  const [arbiterNotFound, setArbiterNotFound] = useState(false);

  // ── Active Directory ──
  const [triggerAdCheck, { data: adCheckData, isFetching: adLoading, isUninitialized: adUninitialized, isError: adIsError }] =
    useLazyGetAdCheckEmailQuery();
  const [adUser, setAdUser] = useState(null);
  const [adNotFound, setAdNotFound] = useState(false);

  // ── GitHub ──
  const [triggerGitHub, { data: githubData, isFetching: githubLoading, isUninitialized: githubUninitialized }] =
    useLazyGetUserGitHubMembershipQuery();
  const [githubUser, setGithubUser] = useState(null);
  const [githubNotFound, setGithubNotFound] = useState(false);

  // ── Accordion open state ──
  const [expanded, setExpanded] = useState({ arbiter: true, ad: true, github: true });

  // Trigger all three searches in parallel when URL param changes
  const lastSearchedEmail = useRef("");
  useEffect(() => {
    if (!searchEmail || !activePlatformId) return;
    if (searchEmail === lastSearchedEmail.current) return;
    lastSearchedEmail.current = searchEmail;

    // Reset state
    setArbiterUser(null);
    setArbiterOrgs([]);
    setArbiterNotFound(false);
    setAdUser(null);
    setAdNotFound(false);
    setGithubUser(null);
    setGithubNotFound(false);

    // 1. Arbiter
    triggerArbiter({ email: searchEmail, platformId: activePlatformId });

    // 2. Active Directory
    triggerAdCheck(searchEmail);

    // 3. GitHub
    triggerGitHub({ email: searchEmail, platformId: activePlatformId });
  }, [searchEmail, activePlatformId]); // eslint-disable-line

  // Process Arbiter result when RTK Query resolves
  useEffect(() => {
    if (arbiterLoading || arbiterUninitialized) return;
    if (!arbiterUsers || !Array.isArray(arbiterUsers) || arbiterUsers.length === 0) {
      setArbiterNotFound(true);
      return;
    }
    const user = arbiterUsers[0];
    setArbiterUser(user);
    setArbiterNotFound(false);
    const rawOrgs = user.platforms?.[activePlatformId]?.organizations;
    if (rawOrgs) {
      setArbiterOrgs(Object.entries(rawOrgs).map(([id, obj]) => ({ id, ...obj })));
    }
  }, [arbiterUsers, arbiterLoading, arbiterUninitialized, activePlatformId]);

  // Process GitHub result when RTK Query resolves
  useEffect(() => {
    if (githubLoading || githubUninitialized) return;
    if (!githubData) {
      setGithubNotFound(true);
      return;
    }
    setGithubUser(githubData);
    setGithubNotFound(false);
  }, [githubData, githubLoading, githubUninitialized]);

  // Process AD result when RTK Query resolves
  useEffect(() => {
    if (adLoading || adUninitialized) return;
    if (adIsError || !adCheckData?.exists_in_ad) {
      setAdNotFound(true);
      return;
    }
    setAdUser(adCheckData);
    setAdNotFound(false);
  }, [adCheckData, adLoading, adUninitialized, adIsError]);

  // Org name lookup: Arbiter ID → name
  const orgIdToName = useMemo(() => {
    if (!allOrgs) return {};
    return allOrgs.reduce((acc, org) => {
      acc[org._id] = org.name;
      return acc;
    }, {});
  }, [allOrgs]);

  // Arbiter "Joined" org names (for discrepancy comparison)
  const arbiterJoinedOrgNames = useMemo(
    () =>
      arbiterOrgs
        .filter((o) => o.membership_status === "Joined")
        .map((o) => orgIdToName[o.id])
        .filter(Boolean),
    [arbiterOrgs, orgIdToName]
  );

  // GitHub org logins
  const githubOrgLogins = useMemo(
    () => (githubUser?.organizations || []).map((o) => o.login),
    [githubUser]
  );

  // Cross-reference: find org mismatches between GitHub and Arbiter
  const discrepancies = useMemo(() => {
    if (!arbiterJoinedOrgNames.length || !githubOrgLogins.length) return null;
    const inGithubNotArbiter = githubOrgLogins.filter(
      (login) => !arbiterJoinedOrgNames.some((name) => caseMatch(name, login))
    );
    const inArbiterNotGithub = arbiterJoinedOrgNames.filter(
      (name) => !githubOrgLogins.some((login) => caseMatch(login, name))
    );
    if (!inGithubNotArbiter.length && !inArbiterNotGithub.length) return null;
    return { inGithubNotArbiter, inArbiterNotGithub };
  }, [arbiterJoinedOrgNames, githubOrgLogins]);

  const handleSearch = () => {
    const trimmed = inputEmail.trim();
    if (!trimmed) return;
    setSearchParams({ email: trimmed });
  };

  const toggle = (key) => (_, v) => setExpanded((prev) => ({ ...prev, [key]: v }));

  const accordionSx = {
    borderRadius: "12px !important",
    border: "1px solid #e2e8f0",
    boxShadow: "none",
    mb: 2,
    "&:before": { display: "none" },
  };

  return (
    <div className="ecr-us">
      {/* Page header */}
      <div className="ecr-ops-page-header">
        <p className="ecr-ops-page-header__eyebrow">ECR Ops Portal</p>
        <h1 className="ecr-ops-page-header__title">User &amp; Organization Search</h1>
        <p className="ecr-ops-page-header__sub">
          Enter an email once — results from Arbiter, Active Directory, and GitHub appear together
        </p>
      </div>

      <div className="ecr-us__body">
        {/* Search bar */}
        <div className="ecr-us__search-bar">
          <TextField
            fullWidth
            size="small"
            placeholder="Enter user email address..."
            value={inputEmail}
            onChange={(e) => setInputEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined style={{ color: "#94a3b8", fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: 14,
                height: 44,
                background: "#fff",
                borderRadius: "10px 0 0 10px",
                "& fieldset": { borderRight: "none" },
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            sx={{
              borderRadius: "0 10px 10px 0",
              background: "#86BC25",
              fontWeight: 700,
              textTransform: "none",
              px: 3,
              fontSize: 14,
              boxShadow: "none",
              height: 44,
              flexShrink: 0,
              "&:hover": { background: "#6a9a1e", boxShadow: "none" },
            }}
          >
            Search
          </Button>
        </div>

        {/* Discrepancy alert */}
        {discrepancies && (
          <div className="ecr-us__discrepancy-alert">
            <div className="ecr-us__discrepancy-heading">
              <WarningAmberOutlined style={{ fontSize: 18, color: "#c2500a" }} />
              <span>Org membership discrepancies detected between GitHub and Arbiter</span>
            </div>
            {discrepancies.inGithubNotArbiter.length > 0 && (
              <div className="ecr-us__discrepancy-row">
                <span className="ecr-us__discrepancy-label ecr-us__discrepancy-label--github">
                  In GitHub, not Joined in Arbiter
                </span>
                <div className="ecr-us__discrepancy-chips">
                  {discrepancies.inGithubNotArbiter.map((o) => (
                    <Chip key={o} label={o} size="small" className="ecr-us__chip ecr-us__chip--warn" />
                  ))}
                </div>
              </div>
            )}
            {discrepancies.inArbiterNotGithub.length > 0 && (
              <div className="ecr-us__discrepancy-row">
                <span className="ecr-us__discrepancy-label ecr-us__discrepancy-label--arbiter">
                  Joined in Arbiter, not in GitHub
                </span>
                <div className="ecr-us__discrepancy-chips">
                  {discrepancies.inArbiterNotGithub.map((o) => (
                    <Chip key={o} label={o} size="small" className="ecr-us__chip ecr-us__chip--error" />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Accordion sections */}
        {searchEmail ? (
          <div className="ecr-us__sections">
            {/* Arbiter */}
            <Accordion expanded={expanded.arbiter} onChange={toggle("arbiter")} sx={accordionSx}>
              <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 56 }}>
                <div className="ecr-us__section-title">
                  <StorageOutlined style={{ fontSize: 20, color: "#64748b" }} />
                  <span>Arbiter</span>
                  <SectionStatus
                    loading={arbiterLoading}
                    found={!!arbiterUser}
                    notFound={arbiterNotFound}
                    issueCount={discrepancies?.inArbiterNotGithub?.length || 0}
                  />
                </div>
              </AccordionSummary>
              <AccordionDetails sx={{ borderTop: "1px solid #f1f5f9", pt: 2, px: 0, pb: 0 }}>
                {arbiterNotFound ? (
                  <p className="ecr-us__empty" style={{ padding: "0 24px 16px" }}>
                    No user found in Arbiter for <strong>{searchEmail}</strong>.
                  </p>
                ) : (
                  <UserOrgSearch initialEmail={searchEmail} hideSearchBar />
                )}
              </AccordionDetails>
            </Accordion>

            {/* Active Directory */}
            <Accordion expanded={expanded.ad} onChange={toggle("ad")} sx={accordionSx}>
              <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 56 }}>
                <div className="ecr-us__section-title">
                  <BadgeOutlined style={{ fontSize: 20, color: "#64748b" }} />
                  <span>Active Directory</span>
                  <SectionStatus
                    loading={adLoading}
                    found={!!adUser}
                    notFound={adNotFound}
                    error={adIsError}
                  />
                </div>
              </AccordionSummary>
              <AccordionDetails sx={{ borderTop: "1px solid #f1f5f9", pt: 2 }}>
                {adUser ? (
                  <ADSection user={adUser} />
                ) : adNotFound ? (
                  <p className="ecr-us__empty">
                    No user found in Active Directory for <strong>{searchEmail}</strong>.
                  </p>
                ) : adIsError ? (
                  <p className="ecr-us__empty ecr-us__empty--error">Failed to fetch from Active Directory.</p>
                ) : null}
              </AccordionDetails>
            </Accordion>

            {/* GitHub */}
            <Accordion expanded={expanded.github} onChange={toggle("github")} sx={accordionSx}>
              <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 56 }}>
                <div className="ecr-us__section-title">
                  <GitHub style={{ fontSize: 20, color: "#64748b" }} />
                  <span>GitHub</span>
                  <SectionStatus
                    loading={githubLoading}
                    found={!!githubUser}
                    notFound={githubNotFound}
                    issueCount={discrepancies?.inGithubNotArbiter?.length || 0}
                  />
                </div>
              </AccordionSummary>
              <AccordionDetails sx={{ borderTop: "1px solid #f1f5f9", pt: 2 }}>
                {githubUser ? (
                  <GitHubSection user={githubUser} arbiterJoinedOrgNames={arbiterJoinedOrgNames} />
                ) : githubNotFound ? (
                  <p className="ecr-us__empty">
                    No user found in GitHub for <strong>{searchEmail}</strong>.
                  </p>
                ) : null}
              </AccordionDetails>
            </Accordion>
          </div>
        ) : (
          <div className="ecr-us__placeholder">
            <SearchOutlined style={{ fontSize: 52, color: "#cbd5e1" }} />
            <p>Enter an email address above to search Arbiter, Active Directory, and GitHub simultaneously.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EcrOpsUnifiedSearch;
